"use client";

import { FlagIcons } from "@/components/icons/icons";
import { SubmitBtn } from "@/components/layout/SubmitBtn";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { languages, t } from "@/i18n/i18n";
import { login } from "@/server-actions/auth/auth";
import Link from "@/i18n/i18n-link";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useFormState } from "react-dom";

export default function Login({ params }: { params: { lang: string } }) {
  const [email, setEmail] = useState<string>();
  const [pw, setPw] = useState<string>();
  const router = useRouter();
  const searchParams = useSearchParams();
  let { lang } = params;
  lang = lang ?? "en";

  interface IError {
    email: string[];
    pw: string[];
  }

  interface IResponse {
    errors: Partial<IError>;
    successful: boolean | null;
    userId?: string;
    email?: string;
  }

  const initialState: IResponse = {
    errors: {
      email: [""],
      pw: [""],
    },
    successful: null,
  };

  // @ts-expect-error
  const [state, formAction] = useFormState(login, initialState);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (state.successful) {
      // login ok
      setError(false);
      localStorage.setItem("userId", state.userId ?? "");
      localStorage.setItem("userEmail", state.email ?? "");
      const redirectPath = searchParams.get("next");
      const isValidRedirect =
        redirectPath &&
        redirectPath.startsWith("/") &&
        !redirectPath.includes("/auth/login") &&
        !redirectPath.startsWith("//");

      if (isValidRedirect) {
        router.push(redirectPath);
        return;
      }

      router.push(`/${lang}`);
    } else if (state.successful === false) {
      // login n-ok
      setError(true);
    }
  }, [lang, router, searchParams, state]);

  const [langIdx, setLangIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setLangIdx((prev) => (prev + 1) % languages.length);
    }, 2000); // 500 ms

    return () => clearInterval(timer);
  }, []);

  const currentLang = languages[langIdx];

  return (
    <div className="flex justify-center items-center bg-background">
      <Card className="w-full max-w-md p-6 space-y-4">
        <div className="text-center space-y-2">
          <div className="flex flex-row justify-between">
            <Image
              src="https://images.gelitabrasil.tech/gelita"
              alt={t("login", "logoAlt", lang)}
              width={165}
              height={90}
            />
            <Image
              src="https://images.gelitabrasil.tech/bipp"
              alt={t("login", "logoAlt", lang)}
              width={185}
              height={85}
            />
          </div>
          <div className="flex flex-col gap-1 items-center justify-center">
            <div className="flex flex-row items-center gap-2 text-lg">
              <span>👋</span>
              <span>
                {currentLang === "en" ? (
                  <FlagIcons.uk className="w-6 h-6" />
                ) : currentLang === "de" ? (
                  <FlagIcons.deutschland className="w-6 h-6" />
                ) : (
                  <FlagIcons.brasil className="w-6 h-6" />
                )}
              </span>
            </div>
            <h1 className="text-3xl font-bold transition-opacity duration-300 ease-in-out">
              {t("login", "title", currentLang)}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {t("login", "description", lang)}
          </p>
          <span hidden={!error} className={"text-destructive mt-3"}>
            {t("login", "loginError", lang)}
          </span>
        </div>

        <form className="space-y-4" action={formAction}>
          <input hidden name="lang" value={lang} readOnly />
          <div className="space-y-2">
            <Label htmlFor="email">{t("login", "labelEmail", lang)}</Label>
            <Input
              name="email"
              type="email"
              placeholder={t("login", "placeholderEmail", lang)}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">
              {t("login", "labelPassword", lang)}
            </Label>
            <Input
              name="password"
              type="password"
              placeholder={t("login", "placeholderPassword", lang)}
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <SubmitBtn
            className="w-full"
            loadingText={t("login", "loadingText", lang)}
            text={t("login", "loginButton", lang)}
          />
        </form>
        <div className="text-center">
          <Link className="text-sm underline" href="/auth/forgot-password">
            {t("forgotpw", "link", lang)}
          </Link>
        </div>
      </Card>
    </div>
  );
}
